"use client";

import { formatWeekLabel } from "./best-time-utils";

type Props = {
  weekStarts: string[];
  windowStart: number | null;
  windowEnd: number | null;
  onWindowChange: (start: number | null, end: number | null) => void;
};

export function WeekPicker({
  weekStarts,
  windowStart,
  windowEnd,
  onWindowChange,
}: Props) {
  const hasWindow = windowStart !== null && windowEnd !== null;

  function handleStartChange(e: React.ChangeEvent<HTMLSelectElement>) {
    const val = e.target.value;
    if (val === "") {
      onWindowChange(null, null);
      return;
    }
    const start = Number(val);
    const end = windowEnd !== null && windowEnd >= start ? windowEnd : weekStarts.length - 1;
    onWindowChange(start, end);
  }

  function handleEndChange(e: React.ChangeEvent<HTMLSelectElement>) {
    const val = e.target.value;
    if (val === "") {
      onWindowChange(null, null);
      return;
    }
    const end = Number(val);
    const start = windowStart !== null && windowStart <= end ? windowStart : 0;
    onWindowChange(start, end);
  }

  return (
    <div className="mb-6">
      <div className="text-[13px] font-semibold text-navy uppercase tracking-[1.2px] mb-3">
        Date range
      </div>
      <div className="flex flex-wrap items-center gap-3">
        <label className="flex items-center gap-2">
          <span className="text-[12px] text-text-muted font-medium">From</span>
          <select
            value={windowStart !== null ? String(windowStart) : ""}
            onChange={handleStartChange}
            className="border border-border-light rounded-[10px] px-3 py-2 text-[13px] bg-white text-navy appearance-none cursor-pointer pr-8 bg-[url('data:image/svg+xml;charset=UTF-8,%3Csvg%20width%3D%2212%22%20height%3D%2212%22%20viewBox%3D%220%200%2012%2012%22%20fill%3D%22none%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%3E%3Cpath%20d%3D%22M3%205l3%203%203-3%22%20stroke%3D%22%237A7A7A%22%20stroke-width%3D%221.5%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%2F%3E%3C%2Fsvg%3E')] bg-[length:12px] bg-[right_8px_center] bg-no-repeat"
          >
            <option value="">Start week</option>
            {weekStarts.map((ws, idx) => (
              <option key={ws} value={String(idx)}>
                {formatWeekLabel(ws)}
              </option>
            ))}
          </select>
        </label>
        <label className="flex items-center gap-2">
          <span className="text-[12px] text-text-muted font-medium">To</span>
          <select
            value={windowEnd !== null ? String(windowEnd) : ""}
            onChange={handleEndChange}
            className="border border-border-light rounded-[10px] px-3 py-2 text-[13px] bg-white text-navy appearance-none cursor-pointer pr-8 bg-[url('data:image/svg+xml;charset=UTF-8,%3Csvg%20width%3D%2212%22%20height%3D%2212%22%20viewBox%3D%220%200%2012%2012%22%20fill%3D%22none%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%3E%3Cpath%20d%3D%22M3%205l3%203%203-3%22%20stroke%3D%22%237A7A7A%22%20stroke-width%3D%221.5%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%2F%3E%3C%2Fsvg%3E')] bg-[length:12px] bg-[right_8px_center] bg-no-repeat"
          >
            <option value="">End week</option>
            {weekStarts.map((ws, idx) => (
              <option key={ws} value={String(idx)}>
                {formatWeekLabel(ws)}
              </option>
            ))}
          </select>
        </label>
        {hasWindow && (
          <button
            onClick={() => onWindowChange(null, null)}
            className="text-[13px] text-gold hover:text-gold-light font-medium transition-colors"
          >
            Clear
          </button>
        )}
      </div>
      {hasWindow && (
        <p className="text-[12px] text-text-muted mt-2">
          Showing {formatWeekLabel(weekStarts[windowStart])} to{" "}
          {formatWeekLabel(weekStarts[windowEnd])}
        </p>
      )}
    </div>
  );
}
